// Company Information — the agency's quick-reference directory of carrier /
// portal phone numbers, links, emails and training-video links (Lissette's
// monday.com "NUMEROS Y LINKS DE COMPAÑIAS" sheet). Sits right below Vault.
//
// Plain reference data (no PHI, no secrets), server-backed via /api/v1/company
// and gated by the vault.* seat permissions. Three ways to add a row: ➕ manual,
// 📥 import Excel/CSV (her monday format, 3 sections auto-detected), and inline
// edit. Every create / update / delete is recorded in each row's `history`
// (shown in the 🕓 popup) AND in the append-only activity log. Nothing is
// hard-deleted and a delete always asks for confirmation.

import { guardarColumnasDeAjuste } from '../columnasDeTabla.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Building2, Plus, Upload, Download, Search, Pencil, Trash2, X, Clock,
  Phone, Mail, Link as LinkIcon, GraduationCap, ExternalLink, Columns as Columns3,
} from 'lucide-react';
import { Modal, confirmDialog, NoteEditor, stripNoteMarks } from './ui.jsx';
import { toast } from './Toast.jsx';
import CustomColumnsManager from './CustomColumnsManager.jsx';
import PhoneText from './PhoneText.jsx';
import { CampoDeColumnaPropia } from './CeldaPropia.jsx';
import { estaMarcada, normalizeColumns, opcionesDeColumna, textoDeCasilla, tipoDeColumna } from '../customColumns.js';
import { cabecerasDe, parseCompanyRows } from '../companyImport.js';
import {
  listCompanyInfo, createCompanyInfo, bulkCreateCompanyInfo,
  updateCompanyInfo, deleteCompanyInfo, renameCompanyCategory,
  getAgencySettings, updateAgencySettings,
} from '../api.js';

const inputStyle = { width: '100%', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem .7rem', color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box' };
const labelStyle = { fontSize: '.68rem', color: 'var(--c-text3,#45688a)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' };

// LAS CATEGORÍAS LAS CREA CADA UNO, como en la Bóveda (Lissette 2026-10-15:
// "no me gustó como hiciste la categoría, lo quería como la bóveda, que uno
// mismo la calificara, por campaña o por broker, no por teléfono o link").
//
// Ya NO hay categorías de fábrica. Las tres de antes —'contacts' (Teléfonos y
// correos), 'links' y 'training' (Videos)— clasificaban por el TIPO de dato, y
// eso ya lo dicen las columnas. Las filas que todavía las llevan guardadas se
// enseñan como "Sin categoría" hasta que alguien les ponga una propia; no se
// reescribe nada en la base.
//
// No hay catálogo aparte: cada fila guarda la suya como texto y la lista sale
// de las filas. Se agrupan sin mirar mayúsculas ("Ventas" = "ventas").
const VIEJAS = ['contacts', 'links', 'training'];
const SIN_CATEGORIA = '__sin__';
const esSinCategoria = (c) => { const t = String(c || '').trim(); return !t || VIEJAS.includes(t); };
const catLabel = (c, es) => (esSinCategoria(c) || c === SIN_CATEGORIA ? (es ? 'Sin categoría' : 'Uncategorized') : String(c).trim());
function categoriasPropiasDe(rows) {
  const vistas = new Map();
  for (const r of rows || []) {
    const c = String((r && r.category) || '').trim();
    if (esSinCategoria(c) || vistas.has(c.toLowerCase())) continue;
    vistas.set(c.toLowerCase(), c);
  }
  return [...vistas.values()].sort((a, b) => a.localeCompare(b));
}
// La categoría con la que se agrupa una fila: la propia escrita como la lista
// (sin mirar mayúsculas), o SIN_CATEGORIA.
function grupoDe(r, propias) {
  const c = String((r && r.category) || '').trim();
  if (esSinCategoria(c)) return SIN_CATEGORIA;
  return propias.find((x) => x.toLowerCase() === c.toLowerCase()) || c;
}
const NUEVA_CATEGORIA = '__nueva__';

function fmtStamp(at) {
  try { return new Date(at).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return String(at || ''); }
}

// Split a free-text cell that may hold several links/emails (newlines, " / ",
// commas, " - ") into individual clickable pieces.
function splitPieces(v) {
  return String(v || '')
    .split(/[\n;]|\s*\/\s*|\s+-\s+|,\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function ensureHttp(u) {
  const s = String(u || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(s)) return 'https://' + s;
  return s;
}

// Render a links / training cell: each URL piece a new-tab anchor, plain text
// otherwise.
function LinksCell({ value }) {
  const pieces = splitPieces(value);
  if (!pieces.length) return <span style={{ color: 'var(--c-text3,#45688a)' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {pieces.map((p, i) => {
        const href = ensureHttp(p);
        const isUrl = /^https?:\/\//i.test(href);
        return isUrl ? (
          <a key={i} href={href} target="_blank" rel="noopener noreferrer"
            style={{ color: 'var(--c-cyan,#00d4ff)', fontSize: '.78rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, wordBreak: 'break-all' }}>
            <ExternalLink size={11} style={{ flexShrink: 0 }} />{p.length > 48 ? p.slice(0, 46) + '…' : p}
          </a>
        ) : (
          <span key={i} style={{ color: 'var(--c-text,#c8dff0)', fontSize: '.78rem', wordBreak: 'break-all' }}>{p}</span>
        );
      })}
    </div>
  );
}

function EmailCell({ value }) {
  const pieces = splitPieces(value);
  if (!pieces.length) return <span style={{ color: 'var(--c-text3,#45688a)' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {pieces.map((p, i) => p.includes('@') ? (
        <a key={i} href={`mailto:${p}`} style={{ color: 'var(--c-cyan,#00d4ff)', fontSize: '.78rem', textDecoration: 'none', wordBreak: 'break-all' }}>{p}</a>
      ) : (
        <span key={i} style={{ color: 'var(--c-text,#c8dff0)', fontSize: '.78rem', wordBreak: 'break-all' }}>{p}</span>
      ))}
    </div>
  );
}

// ── Add / edit form ──────────────────────────────────────────────────────────
function CompanyFormModal({ row, lang, customCols = [], opcionesPorColumna = {}, categoriasPropias = [], onClose, onSaved }) {
  const es = lang === 'es';
  const isEdit = !!row?.id;
  const [f, setF] = useState(() => ({
    name: row?.name || '',
    phones: row?.phones || '',
    links: row?.links || '',
    email: row?.email || '',
    trainingUrl: row?.trainingUrl || '',
    category: esSinCategoria(row?.category) ? '' : String(row.category).trim(),
    department: row?.department || '',
    notes: row?.notes || '',
  }));
  const [nuevaCategoria, setNuevaCategoria] = useState('');
  // Per-row custom-column values, keyed by column id.
  const [customData, setCustomData] = useState(() => ({ ...(row?.customData && typeof row.customData === 'object' ? row.customData : {}) }));
  const setCustom = (id, v) => setCustomData((p) => ({ ...p, [id]: v }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setErr(null);
    if (!f.name.trim()) { setErr(es ? 'El nombre de la compañía es obligatorio' : 'Company name is required'); return; }
    // Una categoría nueva escrita igual que una que ya existe (sin mirar
    // mayúsculas) se junta con esa, en vez de crear una casi igual.
    const escrita = nuevaCategoria.trim();
    const existente = categoriasPropias.find((c) => c.toLowerCase() === escrita.toLowerCase());
    const categoria = f.category === NUEVA_CATEGORIA ? (existente || escrita) : f.category;
    if (f.category === NUEVA_CATEGORIA && !categoria) { setErr(es ? 'Escribe el nombre de la categoría nueva' : 'Enter the new category name'); return; }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(),
        phones: f.phones.trim(),
        links: f.links.trim(),
        email: f.email.trim(),
        trainingUrl: f.trainingUrl.trim(),
        category: categoria,
        department: f.department.trim(),
        notes: f.notes.trim(),
      };
      // Only send values for columns that still exist, trimmed.
      if (customCols.length) {
        const cd = {};
        for (const col of customCols) cd[col.id] = String(customData[col.id] ?? '').trim();
        payload.customData = cd;
      }
      const saved = isEdit ? await updateCompanyInfo(row.id, payload) : await createCompanyInfo(payload);
      onSaved(saved);
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? (es ? 'Editar compañía' : 'Edit company') : (es ? 'Nueva compañía' : 'New company')} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem', minWidth: 340 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.7rem' }}>
          <div>
            <label style={labelStyle}>{es ? 'Compañía' : 'Company'} *</label>
            <input type="text" value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus
              placeholder={es ? 'Ej. AETNA BROKER SUPPORT' : 'e.g. AETNA BROKER SUPPORT'} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{es ? 'Categoría' : 'Category'}</label>
            <select value={f.category} onChange={(e) => set('category', e.target.value)} style={inputStyle}>
              <option value="">{es ? '— Sin categoría —' : '— Uncategorized —'}</option>
              {/* La de esta fila aunque ya no la use nadie más: si no, el
                  desplegable enseñaría otra y al guardar se la cambiaría. */}
              {[...new Set([...categoriasPropias, ...(f.category && f.category !== NUEVA_CATEGORIA ? [f.category] : [])])]
                .map((c) => <option key={c} value={c}>🏷️ {c}</option>)}
              <option value={NUEVA_CATEGORIA}>{es ? '➕ Nueva categoría…' : '➕ New category…'}</option>
            </select>
            {f.category === NUEVA_CATEGORIA && (
              <input type="text" value={nuevaCategoria} onChange={(e) => setNuevaCategoria(e.target.value)} autoFocus maxLength={60}
                placeholder={es ? 'Ej. una campaña, un broker…' : 'e.g. a campaign, a broker…'} style={{ ...inputStyle, marginTop: 6 }} />
            )}
          </div>
        </div>
        <div>
          <label style={labelStyle}>{es ? 'Departamento' : 'Department'}</label>
          <input type="text" value={f.department} onChange={(e) => set('department', e.target.value)} maxLength={300}
            placeholder={es ? 'Ej. Ventas, Servicio al cliente, Comisiones' : 'e.g. Sales, Customer service, Commissions'} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>{es ? 'Teléfonos' : 'Phones'}</label>
          <textarea value={f.phones} onChange={(e) => set('phones', e.target.value)} rows={2}
            placeholder={es ? 'Uno o varios, separados por / o salto de línea' : 'One or more, separated by / or new line'}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
        <div>
          <label style={labelStyle}>{es ? 'Links' : 'Links'}</label>
          <textarea value={f.links} onChange={(e) => set('links', e.target.value)} rows={2}
            placeholder="https://..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
        <div>
          <label style={labelStyle}>{es ? 'Email' : 'Email'}</label>
          <textarea value={f.email} onChange={(e) => set('email', e.target.value)} rows={2}
            placeholder="correo@ejemplo.com" style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
        <div>
          <label style={labelStyle}>🎓 {es ? 'Video de entrenamiento (link)' : 'Training video (link)'}</label>
          <input type="text" value={f.trainingUrl} onChange={(e) => set('trainingUrl', e.target.value)}
            placeholder="https://..." style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>{es ? 'Notas' : 'Notes'}</label>
          <NoteEditor value={f.notes} onChange={(v) => set('notes', v)} lang={es ? 'es' : 'en'} rows={2}
            style={{ ...inputStyle, fontFamily: 'inherit' }} />
        </div>
        {customCols.length > 0 && (
          <div style={{ borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: '.7rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.7rem' }}>
            {customCols.map((col) => (
              <div key={col.id}>
                <label style={labelStyle}>{col.name}</label>
                <CampoDeColumnaPropia columna={col} lang={lang} opciones={opcionesPorColumna[col.id] || []}
                  valor={customData[col.id] ?? ''} onChange={(v) => setCustom(col.id, v)}
                  className="" inputStyle={inputStyle} />
              </div>
            ))}
          </div>
        )}
        {err && <div style={{ fontSize: '.78rem', color: 'var(--c-red,#ff3d6b)', background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.55rem .8rem' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem 1rem', color: 'var(--c-text2,#7aa3c0)', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}>{es ? 'Cancelar' : 'Cancel'}</button>
          <button type="button" onClick={save} disabled={saving}
            style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 8, padding: '.55rem 1.2rem', color: '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? (es ? 'Guardando...' : 'Saving...') : (es ? 'Guardar' : 'Save')}</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Gestor de categorías, como el de tipos de la Bóveda ──────────────────────
// Renombrar cambia el texto en todas las filas que lo usan (una llamada).
// Quitar = pasar sus filas a otra categoría o a "Sin categoría"; ninguna fila
// se borra y el nombre sale de la lista en cuanto nadie lo usa.
function CompanyCategoriesManager({ lang, categorias, cuenta, onClose, onRenamed }) {
  const es = lang === 'es';
  const [editando, setEditando] = useState(null);   // { from, to }
  const [quitando, setQuitando] = useState(null);   // { from, to }
  const [busy, setBusy] = useState(false);
  const norm = (x) => String(x || '').trim().toLowerCase();
  const mover = async (from, to, okMsg) => {
    setBusy(true);
    try {
      const { count } = await renameCompanyCategory(from, to);
      await onRenamed();
      toast.success(okMsg(count));
      return true;
    } catch (err) {
      toast.error((es ? 'No se pudo guardar: ' : 'Could not save: ') + (err?.message || ''));
      return false;
    } finally { setBusy(false); }
  };
  const renombrar = async () => {
    const to = editando.to.trim();
    if (!to) { toast.error(es ? 'Escribe un nombre.' : 'Type a name.'); return; }
    // Otra categoría con el mismo nombre las JUNTARÍA sin decirlo: eso es "quitar".
    if (norm(to) !== norm(editando.from) && categorias.some((c) => norm(c) === norm(to))) {
      toast.error(es ? 'Ya hay una categoría con ese nombre. Para juntarlas usa 🗑️ y elige esa.' : 'There is already a category with that name. To merge them, use 🗑️ and pick it.');
      return;
    }
    if (await mover(editando.from, to, (n) => (es ? `Nombre cambiado en ${n} fila(s).` : `Renamed on ${n} row(s).`))) setEditando(null);
  };
  const quitar = async () => {
    const n = cuenta.get(quitando.from) || 0;
    const ok = await confirmDialog({
      title: es ? `¿Quitar la categoría “${quitando.from}”?` : `Remove the category “${quitando.from}”?`,
      message: es ? `Sus ${n} fila(s) pasan a ${quitando.to || 'Sin categoría'}. No se borra ninguna compañía.` : `Its ${n} row(s) move to ${quitando.to || 'Uncategorized'}. No company is deleted.`,
      confirmLabel: es ? 'Quitar' : 'Remove',
      danger: true,
    });
    if (!ok) return;
    if (await mover(quitando.from, quitando.to, (m) => (es ? `${m} fila(s) movidas.` : `${m} row(s) moved.`))) setQuitando(null);
  };
  return (
    <Modal title={es ? 'Categorías' : 'Categories'} onClose={onClose}>
      <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem', lineHeight: 1.5 }}>
        {es ? 'Las categorías las creas tú (por campaña, por broker…) desde ➕ Nueva o al editar una fila, con "➕ Nueva categoría…". Aquí les cambias el nombre o las quitas.'
          : 'You create the categories (by campaign, by broker…) from ➕ New or when editing a row, with "➕ New category…". Here you rename or remove them.'}
      </div>
      {categorias.length === 0 && <div style={{ fontSize: '.8rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Todavía no hay categorías.' : 'No categories yet.'}</div>}
      {categorias.map((t) => (
        <div key={t} style={{ padding: '.45rem 0', borderBottom: '1px solid var(--c-border,#1a3550)' }}>
          {editando && editando.from === t ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <input className="dkt-inp" autoFocus maxLength={60} value={editando.to} style={{ flex: 1 }}
                onChange={(e) => setEditando({ from: t, to: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') renombrar(); if (e.key === 'Escape') setEditando(null); }} />
              <button type="button" className="dkt-btn-p" disabled={busy} onClick={renombrar}>{es ? 'Guardar' : 'Save'}</button>
              <button type="button" className="dkt-btn-s" disabled={busy} onClick={() => setEditando(null)}>{es ? 'Cancelar' : 'Cancel'}</button>
            </div>
          ) : quitando && quitando.from === t ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '.8rem' }}>{es ? 'Pasar sus filas a:' : 'Move its rows to:'}</span>
              <select className="dkt-inp" value={quitando.to} style={{ flex: 1, minWidth: 140 }}
                onChange={(e) => setQuitando({ from: t, to: e.target.value })}>
                <option value="">{es ? '— Sin categoría —' : '— Uncategorized —'}</option>
                {categorias.filter((c) => c !== t).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button type="button" className="dkt-btn-p" disabled={busy} onClick={quitar}>{es ? 'Quitar' : 'Remove'}</button>
              <button type="button" className="dkt-btn-s" disabled={busy} onClick={() => setQuitando(null)}>{es ? 'Cancelar' : 'Cancel'}</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ flex: 1, fontSize: '.85rem' }}>🏷️ {t} <span style={{ color: 'var(--c-text3,#45688a)' }}>({cuenta.get(t) || 0})</span></span>
              <button type="button" className="dkt-btn-s" disabled={busy} title={es ? 'Cambiar el nombre' : 'Rename'} onClick={() => { setQuitando(null); setEditando({ from: t, to: t }); }}>✏️</button>
              <button type="button" className="dkt-btn-s" disabled={busy} title={es ? 'Quitar (pasar sus filas a otra categoría)' : 'Remove (move its rows to another category)'} onClick={() => { setEditando(null); setQuitando({ from: t, to: '' }); }}>🗑️</button>
            </div>
          )}
        </div>
      ))}
    </Modal>
  );
}

// ── Per-row change history ───────────────────────────────────────────────────
const HIST_LABEL = {
  created: { es: 'Creó la compañía', en: 'Created company' },
  updated: { es: 'Editó la compañía', en: 'Edited company' },
  deleted: { es: 'Eliminó la compañía', en: 'Deleted company' },
};
function CompanyHistoryModal({ row, lang, onClose }) {
  const es = lang === 'es';
  const entries = Array.isArray(row?.history) ? [...row.history].reverse() : [];
  return (
    <Modal title={(es ? 'Historial — ' : 'History — ') + (row?.name || '')} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', minWidth: 320 }}>
        <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5 }}>
          {es ? 'Quién creó o modificó esta compañía, con fecha y hora.' : 'Who created or changed this company, with date and time.'}
        </div>
        {entries.length === 0 && <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.82rem', padding: '1rem', textAlign: 'center' }}>{es ? 'Sin movimientos.' : 'No activity.'}</div>}
        {entries.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem', maxHeight: 360, overflowY: 'auto' }}>
            {entries.map((e, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.6rem', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.5rem .7rem' }}>
                <Clock size={14} style={{ color: e.action === 'deleted' ? 'var(--c-red,#ff3d6b)' : 'var(--c-text3,#45688a)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '.82rem', fontWeight: 600, color: e.action === 'deleted' ? 'var(--c-red,#ff3d6b)' : 'var(--c-white,#eef6ff)' }}>{HIST_LABEL[e.action]?.[es ? 'es' : 'en'] || e.action}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>{e.byName || '—'}</div>
                </div>
                <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{fmtStamp(e.at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Excel / CSV importer (her monday 3-section format) ───────────────────────
function parseCsvText(text) {
  const rows = []; let row = []; let cur = ''; let q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) {
      if (ch === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else q = false; }
      else cur += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cur); cur = ''; }
    else if (ch === '\n') { row.push(cur); rows.push(row); row = []; cur = ''; }
    else if (ch !== '\r') cur += ch;
  }
  if (cur !== '' || row.length) { row.push(cur); rows.push(row); }
  return rows;
}

function CompanyImportModal({ lang, categoriasPropias = [], onClose, onDone }) {
  const es = lang === 'es';
  const [step, setStep] = useState('file'); // file | map | preview | done
  const [rows, setRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [err, setErr] = useState(null);
  const [prog, setProg] = useState({ done: 0, total: 0 });
  const fileRef = useRef(null);
  // Victor Romero, 2026-08-21: "une todo en un solo grupo en lugar de elegir el
  // nombre de las pestañas como te permite la pizarra de Bóveda de
  // Contraseñas". Aquí está el equivalente: escoger la categoría y decir a mano
  // qué columna es cada cosa. Ninguna lista de nombres de columna cubre todos
  // los Excel; poder decirlo a mano, sí.
  const [grid, setGrid] = useState([]);
  const [cabeceras, setCabeceras] = useState([]);
  const [mapeo, setMapeo] = useState({});
  const [categoria, setCategoria] = useState('');
  const [filaCabecera, setFilaCabecera] = useState(-1);

  const handleFile = async (file) => {
    if (!file) return;
    setErr(null); setParsing(true);
    try {
      const name = (file.name || '').toLowerCase();
      let grid;
      if (name.endsWith('.csv')) {
        grid = parseCsvText(await file.text());
      } else {
        const buf = new Uint8Array(await file.arrayBuffer());
        const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs');
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false });
      }
      const hoja = grid || [];
      const { cabeceras: cabs, detectado, fila } = cabecerasDe(hoja);
      // El mapeo arranca con lo que se detectó solo: en el archivo de siempre
      // ya viene bien y basta con pulsar Continuar.
      const inicial = {};
      if (detectado) Object.entries(detectado).forEach(([campo, i]) => { if (i >= 0) inicial[i] = campo; });
      setGrid(hoja); setCabeceras(cabs); setMapeo(inicial); setCategoria(''); setFilaCabecera(fila);
      setStep('map');
    } catch (e) { setErr(e.message || String(e)); }
    finally { setParsing(false); }
  };

  const continuar = () => {
    const parsed = parseCompanyRows(grid, { mapeo, categoria, filaCabecera });
    if (!parsed.length) {
      setErr(es
        ? 'Con este reparto de columnas no sale ninguna fila. Revisa cuál es la columna del nombre.'
        : "This column mapping produces no rows. Check which column holds the name.");
      return;
    }
    setErr(null); setRows(parsed); setStep('preview');
  };

  const runImport = async () => {
    if (!rows.length) return;
    setErr(null); setStep('done');
    setProg({ done: 0, total: rows.length });
    try {
      // Chunk so the request stays small; bulk endpoint = one call per chunk.
      const CHUNK = 200;
      let done = 0;
      for (let i = 0; i < rows.length; i += CHUNK) {
        const slice = rows.slice(i, i + CHUNK);
        await bulkCreateCompanyInfo(slice);
        done += slice.length;
        setProg({ done, total: rows.length });
      }
      if (onDone) await onDone();
    } catch (e) { setErr(e.message); }
  };

  return (
    <Modal title={es ? 'Importar Excel / CSV de compañías' : 'Import companies from Excel / CSV'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem', minWidth: 340 }}>
        {err && <div style={{ fontSize: '.8rem', color: 'var(--c-red,#ff3d6b)', background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 8, padding: '.6rem .8rem' }}>{err}</div>}

        {step === 'file' && (
          <>
            <p style={{ fontSize: '.82rem', color: 'var(--c-text2,#7aa3c0)', margin: 0, lineHeight: 1.5 }}>
              {es
                ? 'Sube tu archivo con el mismo formato que ya usas (las 3 secciones: Teléfonos y correos, Links, y Videos de entrenamiento se detectan solas).'
                : 'Upload your file in the format you already use (the 3 sections — Phones & emails, Links, and Training videos — are auto-detected).'}
            </p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={(e) => handleFile(e.target.files?.[0])} />
            <button type="button" disabled={parsing} onClick={() => fileRef.current?.click()}
              style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 9, padding: '.7rem 1rem', color: '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: parsing ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {parsing ? <><span style={{ width: 15, height: 15, border: '2px solid #0a1628', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'dkt-spin .7s linear infinite' }} /> {es ? 'Leyendo…' : 'Reading…'}</> : <><Upload size={16} /> {es ? 'Elegir archivo' : 'Choose file'}</>}
            </button>
          </>
        )}

        {step === 'map' && (
          <>
            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 700, color: 'var(--c-text2,#7aa3c0)', display: 'block', marginBottom: 4 }}>
                {es ? 'Pestaña donde van estas filas' : 'Tab these rows go to'}
              </label>
              <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
                style={{ width: '100%', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.5rem .6rem', color: 'var(--c-text,#c8dff0)', fontSize: '.82rem' }}>
                <option value="">{es ? '— Sin categoría —' : '— Uncategorized —'}</option>
                {categoriasPropias.map((c) => <option key={c} value={c}>🏷️ {c}</option>)}
              </select>
              <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 4 }}>
                {es ? 'Todas las filas del archivo irán a esta categoría. Las categorías nuevas se crean desde ➕ o al editar una fila.' : 'Every row in the file goes to this category. New categories are created from ➕ or by editing a row.'}
              </div>
            </div>
            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 700, color: 'var(--c-text2,#7aa3c0)', display: 'block', marginBottom: 4 }}>
                {es ? 'Qué es cada columna de tu archivo' : 'What each column of your file is'}
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem', maxHeight: 230, overflowY: 'auto' }}>
                {cabeceras.map((h, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem', alignItems: 'center' }}>
                    <span title={h} style={{ fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h || `(${es ? 'columna' : 'column'} ${i + 1})`}</span>
                    <select value={mapeo[i] || ''} onChange={(e) => setMapeo((m) => ({ ...m, [i]: e.target.value }))}
                      style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.35rem .5rem', color: 'var(--c-text,#c8dff0)', fontSize: '.78rem' }}>
                      <option value="">{es ? '— no importar —' : '— skip —'}</option>
                      <option value="name">{es ? 'Nombre' : 'Name'}</option>
                      <option value="phones">{es ? 'Teléfonos' : 'Phones'}</option>
                      <option value="links">{es ? 'Links' : 'Links'}</option>
                      <option value="email">{es ? 'Correo' : 'Email'}</option>
                      <option value="trainingUrl">{es ? 'Video de entrenamiento' : 'Training video'}</option>
                    </select>
                  </div>
                ))}
              </div>
              {!Object.values(mapeo).includes('name') && (
                <div style={{ fontSize: '.74rem', color: 'var(--c-amber,#ffb700)', marginTop: 5 }}>
                  ⚠️ {es ? 'Marca cuál columna es el Nombre, o no se podrá importar.' : 'Mark which column is the Name, or nothing can be imported.'}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '.5rem' }}>
              <button type="button" onClick={() => { setStep('file'); setGrid([]); setErr(null); }}
                style={{ flex: '0 0 auto', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.6rem .9rem', color: 'var(--c-text2,#7aa3c0)', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer' }}>{es ? 'Atrás' : 'Back'}</button>
              <button type="button" onClick={continuar} disabled={!Object.values(mapeo).includes('name')}
                style={{ flex: 1, background: Object.values(mapeo).includes('name') ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)', border: 'none', borderRadius: 9, padding: '.6rem 1rem', color: Object.values(mapeo).includes('name') ? '#0a1628' : 'var(--c-text3,#45688a)', fontWeight: 800, fontSize: '.85rem', cursor: Object.values(mapeo).includes('name') ? 'pointer' : 'not-allowed' }}>
                {es ? 'Continuar' : 'Continue'}
              </button>
            </div>
          </>
        )}

        {step === 'preview' && (
          <>
            <div style={{ fontSize: '.85rem', color: 'var(--c-text,#c8dff0)', fontWeight: 700 }}>
              {es ? `${rows.length} fila(s) encontradas` : `${rows.length} row(s) found`}
            </div>
            <div style={{ fontSize: '.76rem', color: 'var(--c-text2,#7aa3c0)', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <span>🏷️ {categoria || (es ? 'Sin categoría' : 'Uncategorized')}: {rows.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 240, overflowY: 'auto', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.5rem .7rem' }}>
              {rows.slice(0, 12).map((r, i) => (
                <div key={i} style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  • <b style={{ color: 'var(--c-text,#c8dff0)' }}>{r.name}</b>{r.phones ? ` · ${r.phones.split(/\n/)[0]}` : ''}{r.email ? ` · ${r.email}` : ''}{r.trainingUrl ? ' · 🎓' : ''}
                </div>
              ))}
              {rows.length > 12 && <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>… +{rows.length - 12}</div>}
            </div>
            <div style={{ display: 'flex', gap: '.5rem' }}>
              <button type="button" onClick={() => { setStep('map'); setRows([]); setErr(null); }} style={{ flex: '0 0 auto', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.6rem .9rem', color: 'var(--c-text2,#7aa3c0)', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer' }}>{es ? 'Atrás' : 'Back'}</button>
              <button type="button" onClick={runImport}
                style={{ flex: 1, background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 9, padding: '.6rem 1rem', color: '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: 'pointer' }}>
                {es ? `Importar ${rows.length}` : `Import ${rows.length}`}
              </button>
            </div>
          </>
        )}

        {step === 'done' && (
          <>
            <div style={{ fontSize: '.85rem', color: 'var(--c-text,#c8dff0)' }}>
              {prog.done < prog.total ? (es ? `Importando… ${prog.done}/${prog.total}` : `Importing… ${prog.done}/${prog.total}`) : (es ? '¡Listo!' : 'Done!')}
            </div>
            <div style={{ height: 8, background: 'var(--c-surface2,#112538)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${prog.total ? Math.round((prog.done / prog.total) * 100) : 0}%`, background: 'var(--c-cyan,#00d4ff)', transition: 'width .2s' }} />
            </div>
            {prog.done >= prog.total && (
              <button type="button" onClick={onClose} style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 8, padding: '.55rem 1rem', color: '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: 'pointer' }}>{es ? 'Cerrar' : 'Close'}</button>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Main view ────────────────────────────────────────────────────────────────
export default function CompanyInfoView({ lang = 'es', currentUser, readOnly = false }) {
  const es = lang === 'es';
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [editing, setEditing] = useState(null);  // row or {} for new
  const [importing, setImporting] = useState(false);
  const [history, setHistory] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set()); // ids ticked for bulk delete
  const [customCols, setCustomCols] = useState([]); // agency-shared [{id,name}]
  const [colsOpen, setColsOpen] = useState(false);
  const PER_PAGE = 10;

  const load = async () => {
    try { setRows(await listCompanyInfo()); setError(null); }
    catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); }, []);

  // Load the agency-shared custom-column definitions once.
  useEffect(() => {
    let alive = true;
    getAgencySettings()
      .then((s) => { if (alive) setCustomCols(normalizeColumns(s?.companyCustomColumns)); })
      .catch(() => {/* non-fatal: table just shows no custom columns */});
    return () => { alive = false; };
  }, []);

  // Persist a new column list to agency settings (shared across the agency).
  // Solo se borra lo que se borró en la ventana (ticket de Marilyn Sequera,
  // 2026-10-28). Ver crm-app/columnasDeTabla.js.
  const saveColumns = async (cols) => {
    setCustomCols(await guardarColumnasDeAjuste('companyCustomColumns', customCols, cols));
  };

  // Lo que ofrece cada columna de tipo LISTA: lo que la agencia ya escribio en
  // ella, asi el desplegable se va creando solo (Lissette 2026-09-10).
  const opcionesPorColumna = useMemo(() => {
    const m = {};
    for (const col of customCols) {
      if (tipoDeColumna(col) !== 'list') continue;
      m[col.id] = opcionesDeColumna(col, (rows || []).map((r) => r.customData && r.customData[col.id]));
    }
    return m;
  }, [customCols, rows]);

  const categoriasPropias = useMemo(() => categoriasPropiasDe(rows), [rows]);
  // Los botones de categoría, como los de la Bóveda: las propias, cada una con
  // cuántas filas tiene, y al final "Sin categoría" si queda alguna.
  const cuentaPorCategoria = useMemo(() => {
    const cuenta = new Map();
    for (const r of rows || []) {
      const k = grupoDe(r, categoriasPropias);
      cuenta.set(k, (cuenta.get(k) || 0) + 1);
    }
    return cuenta;
  }, [rows, categoriasPropias]);
  const chipsDeCategoria = useMemo(() => [...categoriasPropias, SIN_CATEGORIA]
    .filter((id) => cuentaPorCategoria.get(id))
    .map((id) => ({ id, label: catLabel(id, es), icon: id === SIN_CATEGORIA ? '🗂️' : '🏷️', count: cuentaPorCategoria.get(id) })),
  [categoriasPropias, cuentaPorCategoria, es]);
  const [categoriasOpen, setCategoriasOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = rows || [];
    if (cat !== 'all') list = list.filter((r) => grupoDe(r, categoriasPropias).toLowerCase() === cat.toLowerCase());
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((r) => [r.name, r.department, r.phones, r.links, r.email, r.trainingUrl, r.notes]
        .some((v) => String(v || '').toLowerCase().includes(needle)));
    }
    return list;
  }, [rows, q, cat, categoriasPropias]);

  // Exportar a CSV lo que se ve: respeta la categoría y la búsqueda activas, no
  // la página (una copia de 10 filas de 300 no le sirve a nadie). Aquí no hay
  // nada cifrado —son teléfonos, links y correos de las compañías— así que sale
  // entero, sin PIN, a diferencia del baúl (Lissette 2026-08-14).
  const exportCsv = () => {
    const esc = (v) => { const t = String(v == null ? '' : v); return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t; };
    const header = [
      es ? 'Categoría' : 'Category', es ? 'Compañía' : 'Company', es ? 'Departamento' : 'Department', es ? 'Teléfonos' : 'Phones',
      'Links', es ? 'Correo' : 'Email', es ? 'Video de entrenamiento' : 'Training video',
      es ? 'Notas' : 'Notes', ...customCols.map((col) => col.name),
    ];
    const lines = [header.map(esc).join(',')];
    for (const r of filtered) {
      lines.push([
        catLabel(r.category, es), r.name || '', r.department || '', r.phones || '', r.links || '',
        r.email || '', r.trainingUrl || '', stripNoteMarks(r.notes || ''),
        // `textoDeCasilla` traduce el "1" de una columna de marcar a un "Si"
        // que se pueda leer en la hoja de calculo.
        ...customCols.map((col) => textoDeCasilla(col, r.customData && typeof r.customData === 'object' ? r.customData[col.id] : '', lang)),
      ].map(esc).join(','));
    }
    const BOM = String.fromCharCode(0xFEFF);
    const blob = new Blob([BOM + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `companias-deskte-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // Paginate 10 per page. Reset to page 1 whenever the filter result changes
  // size or the search/category changes so we never land on an empty page.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  useEffect(() => { setPage(1); }, [q, cat]);
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const onSaved = (saved) => {
    if (!saved) return;
    setRows((prev) => {
      const list = prev || [];
      const idx = list.findIndex((r) => r.id === saved.id);
      if (idx >= 0) { const next = [...list]; next[idx] = saved; return next; }
      return [...list, saved];
    });
  };

  const remove = async (row) => {
    const ok = await confirmDialog({
      title: es ? 'Eliminar compañía' : 'Delete company',
      message: es
        ? `¿Seguro que quieres eliminar "${row.name}"? Quedará registrado quién y cuándo la eliminó.`
        : `Delete "${row.name}"? It will be logged who deleted it and when.`,
      danger: true,
      confirmLabel: es ? 'Eliminar' : 'Delete',
    });
    if (!ok) return;
    try {
      await deleteCompanyInfo(row.id);
      setRows((prev) => (prev || []).filter((r) => r.id !== row.id));
      setSelected((prev) => { const n = new Set(prev); n.delete(row.id); return n; });
    } catch (e) { setError(e.message); }
  };

  // ── Bulk selection / delete ──────────────────────────────────────────────
  // Select-all ticks every row in the current filter (across all pages); the
  // per-row checkboxes are shown on the visible page.
  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const filteredIds = filtered.map((r) => r.id);
  const selectedCount = filteredIds.filter((id) => selected.has(id)).length;
  const allSelected = filteredIds.length > 0 && selectedCount === filteredIds.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allSelected) filteredIds.forEach((id) => n.delete(id));
    else filteredIds.forEach((id) => n.add(id));
    return n;
  });
  const bulkRemove = async () => {
    const ids = filtered.filter((r) => selected.has(r.id)).map((r) => r.id);
    if (!ids.length) return;
    const ok = await confirmDialog({
      title: es ? 'Eliminar compañías' : 'Delete companies',
      message: es
        ? `¿Eliminar ${ids.length} compañía(s) seleccionada(s)? Quedará registrado quién y cuándo.`
        : `Delete ${ids.length} selected compan${ids.length === 1 ? 'y' : 'ies'}? It will be logged who and when.`,
      danger: true,
      confirmLabel: es ? 'Eliminar' : 'Delete',
    });
    if (!ok) return;
    const deleted = [];
    let failed = 0;
    for (const id of ids) {
      try { await deleteCompanyInfo(id); deleted.push(id); }
      catch { failed++; }
    }
    const delSet = new Set(deleted);
    setRows((prev) => (prev || []).filter((r) => !delSet.has(r.id)));
    setSelected((prev) => { const n = new Set(prev); deleted.forEach((id) => n.delete(id)); return n; });
    if (failed) setError(es ? `No se pudieron eliminar ${failed} compañía(s).` : `Could not delete ${failed}.`);
  };

  const th = { textAlign: 'left', padding: '.5rem .6rem', fontSize: '.66rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, borderBottom: '1px solid var(--c-border,#1a3550)', whiteSpace: 'nowrap' };
  const td = { padding: '.55rem .6rem', fontSize: '.82rem', color: 'var(--c-text,#c8dff0)', borderBottom: '1px solid var(--c-border,#13283d)', verticalAlign: 'top' };
  const btnGhost = { background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.4rem .5rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' };

  return (
    <div style={{ padding: '1.2rem 1.4rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', flex: 1, minWidth: 220 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg,#00d4ff22,#7c5cff22)', border: '1px solid var(--c-border,#1a3550)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Building2 size={20} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--c-white,#eef6ff)', fontWeight: 800 }}>{es ? 'Información de Compañías' : 'Company Information'}</h1>
            <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Teléfonos, links, correos y videos de entrenamiento de las compañías, en las categorías que tú creas.' : 'Carrier phones, links, emails and training videos, in the categories you create.'}</div>
          </div>
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', gap: '.5rem' }}>
            <button type="button" onClick={() => setColsOpen(true)} title={es ? 'Agregar o quitar columnas de la tabla' : 'Add or remove table columns'}
              style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.55rem .9rem', color: 'var(--c-text,#c8dff0)', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Columns3 size={15} /> {es ? 'Columnas' : 'Columns'}
            </button>
            {(rows || []).length > 0 && (
              <button type="button" onClick={exportCsv} title={es ? 'Exportar a CSV lo que estás viendo' : 'Export what you are seeing to CSV'}
                style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.55rem .9rem', color: 'var(--c-text,#c8dff0)', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Download size={15} /> {es ? 'Exportar' : 'Export'}
              </button>
            )}
            <button type="button" onClick={() => setImporting(true)}
              style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.55rem .9rem', color: 'var(--c-text,#c8dff0)', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Upload size={15} /> {es ? 'Importar' : 'Import'}
            </button>
            <button type="button" onClick={() => setEditing({})}
              style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 9, padding: '.55rem 1rem', color: '#0a1628', fontWeight: 800, fontSize: '.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={15} /> {es ? 'Nueva' : 'New'}
            </button>
          </div>
        )}
      </div>

      {/* Search + category filter */}
      <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', marginBottom: '.8rem' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text3,#45688a)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={es ? 'Buscar compañía, teléfono, correo, link…' : 'Search company, phone, email, link…'}
            style={{ ...inputStyle, paddingLeft: 32 }} />
        </div>
      </div>
      {/* Categorías como botones, igual que los tipos de la Bóveda. */}
      {(rows || []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.45rem', marginBottom: '.8rem' }}>
          {[{ id: 'all', label: es ? 'Todas' : 'All', icon: '🗂️', count: (rows || []).length }, ...chipsDeCategoria].map((chip) => {
            const active = cat === chip.id;
            return (
              <button key={chip.id} type="button" onClick={() => setCat(chip.id)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 20,
                  padding: '.35rem .75rem', fontSize: '.78rem', fontWeight: 700, cursor: 'pointer',
                  background: active ? 'var(--c-cyan,#00d4ff)' : 'var(--c-surface2,#112538)',
                  color: active ? '#0a1628' : 'var(--c-text2,#7aa3c0)',
                  border: '1px solid ' + (active ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'),
                }}>
                <span>{chip.icon}</span>{chip.label}
                <span style={{ fontSize: '.7rem', opacity: .85, background: active ? 'rgba(10,22,40,.18)' : 'var(--c-surface3,#162d44)', borderRadius: 12, padding: '0 .4rem' }}>{chip.count}</span>
              </button>
            );
          })}
          {!readOnly && (
            <button type="button" onClick={() => setCategoriasOpen(true)} title={es ? 'Cambiar el nombre o quitar tus categorías' : 'Rename or remove your categories'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 20, padding: '.35rem .75rem', fontSize: '.78rem', fontWeight: 700, cursor: 'pointer', background: 'transparent', color: 'var(--c-text3,#45688a)', border: '1px dashed var(--c-border,#1a3550)' }}>
              ✏️ {es ? 'Categorías' : 'Categories'}
            </button>
          )}
        </div>
      )}
      {categoriasOpen && (
        <CompanyCategoriesManager lang={lang} categorias={categoriasPropias} cuenta={cuentaPorCategoria}
          onClose={() => setCategoriasOpen(false)}
          onRenamed={async () => { setCat('all'); await load(); }} />
      )}

      {/* Bulk-delete bar — shows when at least one row in the current filter is ticked. */}
      {!readOnly && selectedCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '.8rem', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.55rem .85rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '.82rem', color: 'var(--c-white,#eef6ff)', fontWeight: 700 }}>{es ? `${selectedCount} seleccionada(s)` : `${selectedCount} selected`}</span>
          <button type="button" onClick={() => setSelected(new Set())} style={{ background: 'none', border: 'none', color: 'var(--c-text2,#7aa3c0)', fontSize: '.8rem', cursor: 'pointer', fontWeight: 600 }}>{es ? 'Limpiar' : 'Clear'}</button>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={bulkRemove}
            style={{ background: 'var(--c-red,#ff3d6b)', border: 'none', borderRadius: 8, padding: '.5rem .9rem', color: '#fff', fontWeight: 800, fontSize: '.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Trash2 size={15} /> {es ? `Borrar seleccionadas (${selectedCount})` : `Delete selected (${selectedCount})`}
          </button>
        </div>
      )}

      {error && <div style={{ fontSize: '.82rem', color: 'var(--c-red,#ff3d6b)', background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 8, padding: '.6rem .8rem', marginBottom: '.8rem' }}>{error}</div>}

      {rows === null && !error && <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.85rem', padding: '2rem', textAlign: 'center' }}>{es ? 'Cargando…' : 'Loading…'}</div>}

      {rows && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--c-text3,#45688a)' }}>
          <Building2 size={40} style={{ opacity: .4, marginBottom: '.6rem' }} />
          <div style={{ fontSize: '.9rem', marginBottom: '.3rem' }}>{(rows.length === 0) ? (es ? 'Aún no hay compañías.' : 'No companies yet.') : (es ? 'Sin resultados para tu búsqueda.' : 'No results for your search.')}</div>
          {rows.length === 0 && !readOnly && <div style={{ fontSize: '.78rem' }}>{es ? 'Usa “Importar” para subir tu Excel, o “Nueva” para agregar una.' : 'Use “Import” to upload your Excel, or “New” to add one.'}</div>}
        </div>
      )}

      {rows && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', background: 'var(--c-surface,#0d1f33)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr>
                {!readOnly && (
                  <th style={{ ...th, width: 34, textAlign: 'center' }}>
                    <input type="checkbox" checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll} title={es ? 'Seleccionar todo' : 'Select all'}
                      style={{ cursor: 'pointer', accentColor: 'var(--c-cyan,#00d4ff)', width: 15, height: 15 }} />
                  </th>
                )}
                <th style={th}>{es ? 'Compañía' : 'Company'}</th>
                <th style={th}>{es ? 'Departamento' : 'Department'}</th>
                <th style={th}><Phone size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />{es ? 'Teléfonos' : 'Phones'}</th>
                <th style={th}><LinkIcon size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />Links</th>
                <th style={th}><Mail size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />Email</th>
                <th style={th}><GraduationCap size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />{es ? 'Video entren.' : 'Training video'}</th>
                {customCols.map((col) => <th key={col.id} style={th} title={col.name}>{col.name}</th>)}
                <th style={{ ...th, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((r) => (
                <tr key={r.id} style={selected.has(r.id) ? { background: 'rgba(0,212,255,.06)' } : undefined}>
                  {!readOnly && (
                    <td style={{ ...td, textAlign: 'center' }}>
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)}
                        style={{ cursor: 'pointer', accentColor: 'var(--c-cyan,#00d4ff)', width: 15, height: 15 }} />
                    </td>
                  )}
                  <td style={{ ...td, fontWeight: 700, color: 'var(--c-white,#eef6ff)', minWidth: 160 }}>
                    {r.name}
                    {(r.category && r.category !== 'contacts') && (
                      <div style={{ fontSize: '.64rem', color: 'var(--c-text3,#45688a)', fontWeight: 500, marginTop: 2 }}>{catLabel(r.category, es)}</div>
                    )}
                  </td>
                  <td style={td}>{r.department ? r.department : <span style={{ color: 'var(--c-text3,#45688a)' }}>—</span>}</td>
                  {/* Cada numero se puede llamar con un clic (Lissette
                      2026-08-26). El texto de alrededor — "Ventas:", los
                      saltos de linea, las extensiones — se queda igual: la
                      casilla se lee como siempre, solo que los numeros se
                      pulsan. */}
                  <td style={{ ...td, whiteSpace: 'pre-wrap', fontFamily: 'var(--font-mono)', fontSize: '.78rem' }}>{r.phones ? <PhoneText value={r.phones} /> : <span style={{ color: 'var(--c-text3,#45688a)' }}>—</span>}</td>
                  <td style={td}><LinksCell value={r.links} /></td>
                  <td style={td}><EmailCell value={r.email} /></td>
                  <td style={td}><LinksCell value={r.trainingUrl} /></td>
                  {customCols.map((col) => {
                    const v = (r.customData && typeof r.customData === 'object') ? r.customData[col.id] : '';
                    // Una columna de marcar guarda un "1": pintarlo tal cual no
                    // le dice nada a nadie. Se lee como casilla.
                    if (tipoDeColumna(col) === 'check') return <td key={col.id} style={td}>{estaMarcada(v) ? '☑' : '☐'}</td>;
                    return <td key={col.id} style={td}>{v ? String(v) : <span style={{ color: 'var(--c-text3,#45688a)' }}>—</span>}</td>;
                  })}
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'inline-flex', gap: 5 }}>
                      <button type="button" title={es ? 'Historial' : 'History'} onClick={() => setHistory(r)} style={{ ...btnGhost, color: 'var(--c-text2,#7aa3c0)' }}><Clock size={14} /></button>
                      {!readOnly && <button type="button" title={es ? 'Editar' : 'Edit'} onClick={() => setEditing(r)} style={{ ...btnGhost, color: 'var(--c-cyan,#00d4ff)' }}><Pencil size={14} /></button>}
                      {!readOnly && <button type="button" title={es ? 'Eliminar' : 'Delete'} onClick={() => remove(r)} style={{ ...btnGhost, color: 'var(--c-red,#ff3d6b)' }}><Trash2 size={14} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination — 10 per page */}
      {rows && filtered.length > PER_PAGE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.6rem', marginTop: '.8rem' }}>
          <div style={{ fontSize: '.76rem', color: 'var(--c-text3,#45688a)' }}>
            {es
              ? `Mostrando ${(safePage - 1) * PER_PAGE + 1}–${Math.min(safePage * PER_PAGE, filtered.length)} de ${filtered.length}`
              : `Showing ${(safePage - 1) * PER_PAGE + 1}–${Math.min(safePage * PER_PAGE, filtered.length)} of ${filtered.length}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
            <button type="button" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ ...btnGhost, padding: '.4rem .7rem', fontSize: '.78rem', fontWeight: 700, color: safePage <= 1 ? 'var(--c-text3,#45688a)' : 'var(--c-text,#c8dff0)', cursor: safePage <= 1 ? 'not-allowed' : 'pointer', opacity: safePage <= 1 ? 0.5 : 1 }}>
              ‹ {es ? 'Anterior' : 'Prev'}
            </button>
            <span style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', fontFamily: 'var(--font-mono)', minWidth: 60, textAlign: 'center' }}>{safePage} / {totalPages}</span>
            <button type="button" disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{ ...btnGhost, padding: '.4rem .7rem', fontSize: '.78rem', fontWeight: 700, color: safePage >= totalPages ? 'var(--c-text3,#45688a)' : 'var(--c-text,#c8dff0)', cursor: safePage >= totalPages ? 'not-allowed' : 'pointer', opacity: safePage >= totalPages ? 0.5 : 1 }}>
              {es ? 'Siguiente' : 'Next'} ›
            </button>
          </div>
        </div>
      )}

      {editing && <CompanyFormModal row={editing.id ? editing : null} lang={lang} customCols={customCols} opcionesPorColumna={opcionesPorColumna} categoriasPropias={categoriasPropias} onClose={() => setEditing(null)} onSaved={onSaved} />}
      {importing && <CompanyImportModal lang={lang} categoriasPropias={categoriasPropias} onClose={() => setImporting(false)} onDone={async () => { await load(); }} />}
      {history && <CompanyHistoryModal row={history} lang={lang} onClose={() => setHistory(null)} />}
      {colsOpen && (
        <CustomColumnsManager columns={customCols} lang={lang}
          title={es ? 'Columnas de Compañías' : 'Company columns'}
          onClose={() => setColsOpen(false)} onSave={saveColumns} />
      )}
    </div>
  );
}
